import { useChannelTalk } from '@repo/channel-talk';
import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { Button, Description, Divider, Guide, Title } from '@zenterprise-inc/ui';
import Image from 'next/image';
import { useRouter } from 'next/router';

import IllustChatWon from '@/assets/illust-icons/fill-balloon-chat-won-lg.svg';
import LockIcon from '@/assets/illust-icons/fill-lock3.svg';
import ManyCach from '@/assets/illust-icons/fill-manycash-yl-lg.svg';
import ZeroMoney from '@/assets/illust-icons/fill-zero-money.svg';
import s from '@/components/tax-refund/cancel/CancelContent.module.scss';

type CancelContentProps = {
  name: string;
  payAmount: string;
  discountRate?: string;
  resultPath: string;
  reasonPath: string;
  supportBotLink: string;
  titleSuffix: string;
  businessType: 'indis' | 'corps';
  className?: string;
};

export const CancelContent = ({ payAmount, businessType, discountRate, resultPath, reasonPath, supportBotLink, className }: CancelContentProps) => {
  const router = useRouter();
  const { type } = router.query;
  const { openChannelTalk } = useChannelTalk();
  const { sendViewEvent, sendClickEvent } = useUserEventLogger();
  const { showDialog } = useDialog();

  const taxName = type === 'indis' ? '종합소득세' : type === 'indis-tritx' ? '양도소득세' : type === 'corps' ? '법인세' : '';

  const handleContinue = () => {
    sendClickEvent('cancel_defense_keep-going', { business: businessType });
    sendViewEvent('cancel_defense_keep-going-dialog', { business: businessType }, { isAllowRetry: true });
    showDialog({
      title: '환급을 계속 진행할게요',
      description: '신속하고 빠짐없이 환급받으실 수 있도록 최선을 다할게요.',
      buttons: {
        primary: {
          text: '확인',
          onClick: handleOk
        }
      }
    });
  };

  const handleCancel = () => {
    sendClickEvent('cancel_defense_cancel-going', { business: businessType });
    router.push({ pathname: reasonPath, query: router.query });
  };

  const handleOk = () => {
    sendClickEvent('cancel_defense_keep-going-dialog-ok', { business: businessType });
    router.push({ pathname: resultPath, query: { type } });
  };

  const handleAsk = () => {
    sendClickEvent('cancel_defense_cx', { business: businessType });
    openChannelTalk(supportBotLink);
  };

  return (
    <div className={`flex w-full flex-col ${className ?? ''}`}>
      <Description.Guide title={`잠시만요! 지금 되찾지 않으면\n사라질 수 있어요`} titleSize="large" />

      <div className="page-padding flex flex-col items-center justify-center">
        <div className="w-full max-w-[300px] rounded-[2rem] border-[1px] border-yellow-100 bg-yellow-50">
          <div className="mx-7 mb-8 mt-12 flex flex-col items-center justify-center">
            <ManyCach className={s.animate} width={100} height={100} />
            <div className="mb-10" />
            <p className="body-14 text-gray-800">
              <span className="break-all font-bold">{taxName}</span>
              &nbsp;예상 환급액
            </p>
            <div className="mb-1" />
            <p className="headline-26">
              {payAmount}
              <span className="headline-18 text-gray-800">원</span>
            </p>
            <div className="mb-8" />
            <p className="body-13 text-center text-gray-500">
               세법상 경정청구 기간이 지나면
              <br />
              환급을 신청할 수 없어요.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-12" />

      <div className="page-padding py-10">
        <p className="text-14 font-medium text-violet-600">이용료 부담은 덜어두세요</p>
        <p className="headline-20 pb-5 pt-2 text-gray-900">
          환급받기 전까지
          <br />
          어떤 비용도 발생하지 않아요
        </p>
        <div className="flex items-center justify-center rounded-xl bg-blue-50">
          <div className="py-6">
            <ZeroMoney />
          </div>
        </div>
        <div className="mb-5" />
        <p className="text-14 text-gray-800">환급 전까지 드는 비용은 0원! 최종 환급액이 조회금액보다 적거나 없다면 이용료 부담은 그만큼 낮아져요.</p>
        <div className="mb-2" />
        <p className="text-12 text-gray-400">*체납이 있다면 환급액이 자동으로 국세에 충당됩니다.</p>

        {discountRate ? (
          <>
            <div className="mb-5" />
            <Description.Feedback
              text={`지금 취소하시면 ${discountRate}% 이용료 제휴 할인 혜택도 사라져요!`}
              hasBg
              hasLinkIcon={false}
              hasStatusIcon
              status="error"
            />
          </>
        ) : null}
      </div>

      <div className="page-padding py-10">
        <p className="text-14 font-medium text-violet-600">수임 중인 세무사가 있더라도</p>
        <p className="headline-20 pb-5 pt-2 text-gray-900">
          세금 환급은 특화된 전문가의
          <br />
          도움이 필요해요
        </p>
        <div className="flex items-center justify-center rounded-xl bg-violet-50">
          <div className={'py-[27px]'}>
            <Image src="/images/incomeTaxRefund/img_cancel-cpa2.png" width={193} height={80} alt={'cpa'} />
          </div>
        </div>
        <div className="mb-6" />
        <p className="text-14 text-gray-800">
          경정청구는 환급 전문가들의 경험과 노하우가 중요해요.
          <br />
          실력있는 세무사라도 환급 항목을 빠짐없이 찾아내기는 어렵거든요. 수임 중인 세무사 변경 없이 도와드릴게요.
        </p>
      </div>

      <div className="page-padding py-10">
        <p className="text-14 font-medium text-violet-600">사후 관리도 걱정마세요</p>
        <p className="headline-20 pb-5 pt-2 text-gray-900">책임환불제로 더욱 안전해요</p>
        <div className="flex items-center justify-center rounded-xl bg-green-50">
          <div className="py-6">
            <LockIcon />
          </div>
        </div>
        <div className="mb-6" />
        <p className="text-14 text-gray-800">
          특화된 전문가의 꼼꼼한 신고 검토에도 걱정되시나요?
          <br />
          환급 신고로 인해 추납 세액이 발생하면 해당 부분의 이용료는 전액 환불해 드려요.
        </p>
      </div>

      <div className="page-padding py-10">
        <div className="flex items-center justify-center rounded-xl bg-gray-50">
          <Description.Guide title={'97%의 신청 고객이\n환급 계약을 유지하고 있어요!'} imgChild={<IllustChatWon width={60} height={60} />} />
        </div>
      </div>

      <Divider.Section />

      <div className="page-padding pb-16 pt-8">
        <div className="pb-4" />
        <Guide>
          <Guide.Title titleType="large">
            정말로 환급 신청을
            <br />
            취소하시겠어요?
          </Guide.Title>
        </Guide>
        <div className="pb-4" />
        <Button.FullWidth state="primary" onClick={handleContinue}>
          환급 계속 진행하기
        </Button.FullWidth>
        <div className="pb-3" />
        <Button.FullWidth onClick={handleCancel}>환급 취소하기</Button.FullWidth>
      </div>

      <div className="page-padding bg-gray-50 pb-2 pt-5">
        <Title.Section title="취소 유의사항" size="small" />
        <div className="body-14 text-gray-800">
          <ul className="mb-5 list-disc pl-5">
            <li className="mb-2 text-gray-700">환급 신청 취소는 국세청에서 환급이 결정되기 전까지 가능합니다.</li>
            <li className="mb-2 text-gray-700">취소 접수 시 신청 내역 확인이 필요하여 취소 완료까지 3일 정도가 소요될 수 있습니다.</li>
            <li className="mb-2 text-gray-700">제휴 세무사의 신고서 검토 업무가 진행된 경우 업무 진행비가 발생할 수 있습니다.</li>
            <li className="mb-2 text-gray-700">신청을 취소하더라도 추후 다시 환급 신청이 가능합니다.</li>
            <li className="mb-2 text-gray-700">취소 요청 접수 시 제휴 세무사의 검토는 중단되며, 취소가 완료되기까지 별도의 이용료는 추가되지 않습니다.</li>
          </ul>
          <p className="mb-2 text-gray-700">추가로 궁금하신 점이 있으시면 고객센터로 문의해주세요.</p>
          <ul className="mb-5">
            <li className="mb-2">
              <div className="text-violet-600" onClick={handleAsk}>
                고객센터로 문의하기
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
